import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import { CircularProgress, Tooltip } from '@mui/material';
import './index.scss';

import { Client, Provider, Session, SessionType } from '../../../../shared/Assets/sessionDetailsSVGs';
import { CustomAlert, Text } from '../../../../shared/uiComponents';
import { GREEN, ORANGE, PEARL, PRIMARY, RED, TEXT } from '../../../../shared/Helpers/colors';
import { Notes } from '../../../../shared/Assets/menuIcons';
import { errorNormalizer } from '../../../../shared/Helpers/functions';
import { useCheckPermission } from '../../../../shared/Helpers/hooks';
import { PERMISSIONS } from '../../../../App/constants';
import { SoapNoteStatusTypes, backgroundColors, colors } from '../../../../shared/uiComponents/StatusBadge';
import { EditSvg, ViewSvg } from '../../../../shared/Assets/svgIcons';
import { AdminTypes } from '../../../../components/Menu';
import SessionStatusIcon from '../../Session/sessionStatusIcon';
import SessionActions from './sessionActions';
import DeleteSession from './deleteSession';
import AdminActions from './adminActions';
import DateInfo from './dateInfo';
import TimeInfo from './timeInfo';

import { DispatchProperties, useSelector } from '../../../../redux/store';
import { clearSessionInfo } from '../../../../redux/State/clientSlice/sessionSlice';
import { setInfoEvent } from '../../../../redux/State/clientSlice/calendarHelperSlice';
import { SoapNotesStatuses } from '../../../../redux/API/ClientAPIHelpers/dataCollectionProperties';
import { clearErrors, createSoapNote } from '../../../../redux/State/clientSlice/soapNoteSlice';
import { getSoapNotePDF } from '../../../../redux/State/exportSlice';

const textColors = {
	gray: TEXT.SECONDARY,
	primary: PRIMARY.DARK[16],
	success: GREEN.DARK[16],
	warning: ORANGE.DARK[16],
	error: RED.DARK[16],
	solidGray: PEARL.BASE[700],
	solidWarning: ORANGE.BASE,
};

const InfoContent = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const navigate = useNavigate();

	const POST_SUBMIT_EDIT = useCheckPermission(PERMISSIONS.SOAPNOTE.POST_SUBMIT_EDIT);

	const data = useSelector((state) => state.calendarHelper.infoEventData);
	const role = useSelector((state) => state.account.role);
	const userId = useSelector((state) => state.account.user.id);
	const loading = useSelector((state) => state.export.loading);

	const isAdmin = role?.section.id === AdminTypes.admin;
	const isExpired =
		SoapNotesStatuses.expired48 === data?.reportStatus?.status ||
		SoapNotesStatuses.expiredWeek === data?.reportStatus?.status;
	const isSubmitted = SoapNotesStatuses.submitted === data?.reportStatus?.status;

	const canUserEdit = !isExpired && !isSubmitted && data?.user.id === userId;
	const canUserDoSessionActions = isAdmin || data?.user.id === userId;

	const canUserView =
		SoapNotesStatuses.pendingBT === data?.reportStatus?.status ||
		role?.section.id === AdminTypes.bcba ||
		data?.user.id === userId;
	const canAdminEditSubmitted = isAdmin && POST_SUBMIT_EDIT.permissionGranted && isSubmitted;

	const soapNoteStatusType = SoapNoteStatusTypes[data?.reportStatus?.status ?? 0];
	const iconColor = colors[soapNoteStatusType];
	const iconBackgroundColor = backgroundColors[soapNoteStatusType];
	const textColor = textColors[soapNoteStatusType];

	const navigateEditSoapNote = ({
		clientId,
		reportId,
		sessionId,
		postSubmit = false,
	}: {
		clientId: string;
		postSubmit?: boolean;
		reportId: string;
		sessionId: string;
	}) => {
		dispatch(setInfoEvent(false));
		dispatch(clearSessionInfo());
		dispatch(clearErrors());
		const query = new URLSearchParams();
		query.set('clientId', clientId);
		query.set('reportId', reportId);
		query.set('sessionId', sessionId);
		query.set('postSubmit', `${postSubmit}`);
		navigate(`/soap-notes/edit?${query.toString()}`);
	};

	const editSoapNote = () => {
		if (!data) return;
		const { client, reportId, sessionId } = data;
		if (!reportId) return;
		navigateEditSoapNote({ clientId: client.id, reportId, sessionId });
	};

	const editSubmittedSoapNote = () => {
		if (!data) return;
		const { client, reportId, sessionId } = data;
		if (!reportId) return;
		navigateEditSoapNote({
			clientId: client.id,
			reportId,
			sessionId,
			postSubmit: true,
		});
	};

	const viewSoapNoteHandler = () => {
		if (!data?.reportId) return;
		const reportId = data.reportId;
		dispatch(
			getSoapNotePDF({
				reportId,
				includeTrials: true,
			}),
		)
			.then(unwrapResult)
			.then((response) => {
				const objectURL = URL.createObjectURL(response);
				window.open(objectURL);
			})
			.catch(errorNormalizer);
	};

	const createSoapNoteHandler = () => {
		if (!data) return;

		const { client, sessionId } = data;
		dispatch(createSoapNote({ clientId: client.id, sessionId }))
			.then(unwrapResult)
			.then(({ reportId }) => {
				if (!reportId) return;
				navigateEditSoapNote({ clientId: client.id, reportId, sessionId });
			})
			.catch(errorNormalizer);
	};

	const getSoapNoteEditAction = () => {
		if (typeof data?.reportStatus?.status !== 'number') {
			return createSoapNoteHandler;
		}

		return editSoapNote;
	};

	const soapNoteAction = getSoapNoteEditAction();

	return data ? (
		<div className="infoContent">
			{isAdmin && (
				<div className="row">
					<div className="icon">
						<Provider />
					</div>
					<div className="rowContent">
						<div className="text">
							<Text title="Provider" type="pxs" fontWeight="medium" color={TEXT.TERTIARY} />
							<Text title={data.user.fullName} type="psm" fontWeight="semiBold" />
						</div>
					</div>
				</div>
			)}
			<div className="row">
				<div className="icon">
					<Client />
				</div>
				<div className="rowContent">
					<div className="text">
						<Text title="Client" type="pxs" fontWeight="medium" color={TEXT.TERTIARY} />
						<Link to={`/clients/client/${data.client.id}`} target="_blank">
							<Text title={data.client.fullName} type="psm" fontWeight="semiBold" />
						</Link>
					</div>
				</div>
			</div>
			<div className="row">
				<div className="icon">
					<SessionType />
				</div>
				<div className="rowContent">
					<div className="text">
						<Text title="Session type" type="pxs" fontWeight="medium" color={TEXT.TERTIARY} />
						<Text title={data.sessionType.name} type="psm" fontWeight="semiBold" />
					</div>
				</div>
			</div>
			<div className="row">
				<div className="icon">
					<SessionStatusIcon event={data} />
				</div>
				<div className="rowContent">
					<div className="text">
						<Text title="Session Status" type="pxs" fontWeight="medium" color={TEXT.TERTIARY} />
						<Text title={data.isCompleted ? 'Completed' : 'Not Completed'} type="psm" fontWeight="semiBold" />
					</div>
				</div>
			</div>
			<div className="row">
				<div className="icon" style={{ backgroundColor: iconBackgroundColor }}>
					<Notes color={iconColor} />
				</div>
				<div className="rowContent">
					<div className="text">
						<Text title="Soap note" type="pxs" fontWeight="medium" color={TEXT.TERTIARY} />
						<Text
							title={data.reportStatus ? data.reportStatus.name : 'Not Created'}
							type="psm"
							fontWeight="semiBold"
							color={textColor}
						/>
					</div>
					{Boolean(data.sessionId) &&
						(loading ? (
							<div className="actionIcon" style={{ color: PRIMARY.DARK[16] }}>
								<CircularProgress size="16px" color="inherit" />
							</div>
						) : isAdmin ? (
							<>
								{isSubmitted && (
									<div className="actionIcon" onClick={viewSoapNoteHandler}>
										<ViewSvg />
									</div>
								)}
								{isSubmitted && canAdminEditSubmitted && !isExpired && (
									<div className="actionIcon" onClick={editSubmittedSoapNote}>
										<EditSvg />
									</div>
								)}
								{!isExpired && !isSubmitted && (
									<div className="actionIcon" onClick={soapNoteAction}>
										<EditSvg />
									</div>
								)}
							</>
						) : (
							<>
								{isSubmitted && canUserView && (
									<div className="actionIcon" onClick={viewSoapNoteHandler}>
										<ViewSvg />
									</div>
								)}
								{canUserEdit && (
									<Tooltip
										disableHoverListener={SoapNotesStatuses.pendingBT !== data?.reportStatus?.status}
										title="You cannot edit notes in Pending BT status."
									>
										<div className="actionIcon" onClick={soapNoteAction}>
											<EditSvg />
										</div>
									</Tooltip>
								)}
							</>
						))}
				</div>
			</div>
			<DateInfo />
			<TimeInfo />
			{canUserDoSessionActions && (
				<div className="sessionActions">
					<DeleteSession />
					<SessionActions />
					{isAdmin && <AdminActions />}
				</div>
			)}
		</div>
	) : (
		<Text title="No Information" center className="padding16" type="psm" />
	);
};

const InfoEvent = () => {
	const dispatch = useDispatch<DispatchProperties>();

	const open = useSelector((state) => state.calendarHelper.popups.info);

	const closeHandler = () => {
		dispatch(setInfoEvent(false));
	};

	return (
		<CustomAlert
			open={open}
			onClose={closeHandler}
			titleOnTop={false}
			title="Session Details"
			Content={InfoContent}
			Icon={() => <Session />}
		/>
	);
};

export default InfoEvent;
